import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "react-quill/dist/quill.snow.css";
import { getUsers, updateUser } from "../Redux/Reducers/AuthSlice";
import DefaultAvatar from "../otherImages/default.png";

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  if (Array.isArray(error?.errors?.image)) return error.errors.image[0];
  return error?.message || fallback;
};

const EditUserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const { users = [], loading, error, updatingUser } = useSelector(
    (state) =>
      state.auth || {
        users: [],
        loading: false,
        error: null,
        updatingUser: false,
      }
  );

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    image: null,
    image_url: null,
  });

  const [originalData, setOriginalData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    image_url: null,
  });

  const [isFormModified, setIsFormModified] = useState(false);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (users && users.length > 0 && id) {
      const selectedUser = users.find((u) => String(u.id) === String(id));
      if (selectedUser) {
        const userData = {
          first_name: selectedUser.first_name || "",
          last_name: selectedUser.last_name || "",
          email: selectedUser.email || "",
          image: null,
          image_url: selectedUser.image_url || selectedUser.image || null,
        };
        setFormData(userData);
        setOriginalData({
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          image_url: userData.image_url,
        });
      }
    }
  }, [users, id]);

  // Check if form data changed
  useEffect(() => {
    const hasChanges = 
      formData.first_name !== originalData.first_name ||
      formData.last_name !== originalData.last_name ||
      formData.image !== null;
    
    setIsFormModified(hasChanges);
  }, [formData, originalData]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = new FormData();
    submitData.append('first_name', formData.first_name);
    submitData.append('last_name', formData.last_name);
    submitData.append('email', formData.email);
    
    if (formData.image) {
      submitData.append('image', formData.image);
    }

    try {
      const response = await dispatch(updateUser({ id, data: submitData })).unwrap();
      
      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "User Updated Successfully!",
          text: response?.message || "User has been updated successfully.",
          timer: 2000,
          showConfirmButton: false,
        });
        
        navigate(location.state?.from || "/users");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: response?.message || "Failed to update user.",
        }).then(() => {
          window.location.reload();
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: getErrorMessage(error, "Something went wrong while updating the user."),
      }).then(() => {
        window.location.reload();
      });
    }
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading user data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4 text-center py-5">
        <div className="text-danger">
          <p>Error: {typeof error === 'string' ? error : 'Failed to load user data'}</p>
          <button 
            className="btn btn-primary mt-3"
            onClick={() => dispatch(getUsers())}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="container mt-4 mainForm">
      <div className="imageEdit text-center mb-4 position-relative d-inline-block">
        <img
          src={
            formData.image instanceof File
              ? URL.createObjectURL(formData.image)
              : formData.image_url || DefaultAvatar
          }
          alt="userImage"
          className="rounded-circle"
          style={{
            width: "100px",
            height: "100px",
            objectFit: "cover",
            boxShadow: "1px 1px 10px #00000036",
          }}
        />
        <input
          type="file"
          id="userImageInput"
          name="image"
          accept="image/*"
          onChange={handleChange}
          hidden
        />
        <label
          htmlFor="userImageInput"
          className="position-absolute"
          style={{
            bottom: "0px",
            right: "calc(0% - 0px)",
            background: "#000",
            borderRadius: "50%",
            padding: "10px",
            cursor: "pointer",
            boxShadow: "0 0 0 4px white",
            fontSize: "0px",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="white"
            className="bi bi-pencil"
            viewBox="0 0 16 16"
          >
            <path d="M12.146.854a.5.5 0 0 1 .708 0l2.292 2.292a.5.5 0 0 1 0 .708l-9.5 9.5a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l9.5-9.5zM11.207 2L3 10.207V11h.793L13 2.793 11.207 2zM14 3.5 12.5 2 13 1.5 14.5 3 14 3.5z" />
          </svg>
        </label>
      </div>

      <div className="row">
        <div className="mb-3 col-md-6">
          <label htmlFor="first_name" className="form-label">
            First Name <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="first_name"
            name="first_name"
            value={formData.first_name}
            required
            onChange={handleChange}
            placeholder="First Name"
          />
        </div>
        <div className="mb-3 col-md-6">
          <label htmlFor="last_name" className="form-label">
            Last Name <span>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="last_name"
            name="last_name"
            value={formData.last_name}
            required
            onChange={handleChange}
            placeholder="Last Name"
          />
        </div>
      </div>

      <div className="row">
        <div className="mb-3">
          <label htmlFor="email" className="form-label">
            Email Address <span>*</span>
          </label>
          <input
            type="email"
            className="form-control"
            id="email"
            name="email"
            value={formData.email}
            required
            disabled
            readOnly
            placeholder="Email Address"
          />
        </div>
      </div>

      <button 
        type="submit" 
        className="btn btn-primary"
        disabled={!isFormModified || updatingUser}
      >
        {updatingUser ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

export default EditUserForm;
